import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import { trackEvent } from '../../utils/analytics';
import { useCart } from '../../context/CartContext';
import Breadcrumb from '../../components/Breadcrumb';
import './Solutions.scss';

const AISecurity = () => {
  const { addToCart, removeFromCart, isInCart } = useCart();

  useEffect(() => {
    setPageTitle('AI Security Solutions');
  }, []);

  const handleCartAction = (solutionType, title) => {
    const itemId = `ai-security-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    if (isItemInCart) {
      trackEvent('remove_from_cart', 'AI Security Solutions', {
        solution_name: 'AI Security',
        solution_type: solutionType,
        source_page: 'ai_security_solutions',
      });
      removeFromCart(itemId);
    } else {
      trackEvent('add_to_cart', 'AI Security Solutions', {
        solution_name: 'AI Security',
        solution_type: solutionType,
        source_page: 'ai_security_solutions',
      });
      addToCart({
        id: itemId,
        title,
        type: 'AI Security Solution',
      });
    }
  };

  const renderCartButton = (solutionType, title) => {
    const itemId = `ai-security-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    return (
      <button
        className="card__link card__link--cart"
        onClick={() => handleCartAction(solutionType, title)}
      >
        {isItemInCart ? 'Remove from shopping cart' : 'Add to shopping cart'}
      </button>
    );
  };

  const breadcrumbs = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/solutions/ai-security', label: 'AI Security' },
  ];

  return (
    <div className="solutions-page">
      <Breadcrumb items={breadcrumbs} />
      <section className="page-header section section--light">
        <div className="container">
          <p className="section__subtitle">AI/ML Security and Privacy</p>
          <h1 className="page-header__title">AI Security Solutions</h1>
          <p className="page-header__description">
            We help you implement secure and privacy-enhanced AI solutions, including LLMs and
            custom models. Our expertise covers secure model deployment, robust guardrails setup,
            and comprehensive operational governance.
          </p>
          <blockquote className="section__quote">
            Our AI security solutions are designed to help you leverage AI technology safely and
            responsibly. We work with you to understand your specific needs and implement solutions
            that protect your data and models while enabling innovation.
          </blockquote>
        </div>
      </section>

      <section className="solutions-content section section--light">
        <div className="container">
          <div className="card solution-card">
            <h3 className="card__title">Secure AI Model Deployment</h3>
            <p className="card__description">
              We help you deploy AI models securely in your cloud environment, implementing robust
              security controls, access management, and monitoring. Our approach ensures your models
              are protected while remaining accessible to authorized users.
            </p>
            {renderCartButton('model_deployment', 'Secure AI Model Deployment')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AI Privacy Enhancement</h3>
            <p className="card__description">
              We implement privacy-enhancing technologies for your AI systems, including data
              anonymization, differential privacy, and secure enclaves. Our solutions help protect
              sensitive data while maintaining model effectiveness.
            </p>
            {renderCartButton('privacy_enhancement', 'AI Privacy Enhancement')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AI Guardrails Implementation</h3>
            <p className="card__description">
              We set up comprehensive guardrails for your AI systems, including input validation,
              output filtering, and usage monitoring. Our approach helps prevent misuse while
              ensuring your AI systems operate within defined boundaries.
            </p>
            {renderCartButton('guardrails', 'AI Guardrails Implementation')}
          </div>

          <div className="card solution-card">
            <h3 className="card__title">AI Governance Framework</h3>
            <p className="card__description">
              We help establish AI governance frameworks that align with industry standards and
              regulatory requirements. Our solutions include policies, procedures, and controls for
              responsible AI development and operation.
            </p>
            {renderCartButton('governance', 'AI Governance Framework')}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISecurity;
